/**
 * Client accessors for the per-booking no-show guarantee (backend guarantee endpoints,
 * card-fingerprint-no-show.md). Every call funnels through `@repo/api-client`'s fetch mutator (bearer
 * token, RFC 9457 `ApiError`, auto Idempotency-Key on writes). The charge is manager-step-up-gated
 * (`@RequiresStepUp(NO_SHOW_CHARGE)`, R6) — the minted authorization rides as the `X-Step-Up-Authorization`
 * header (BK-8 seam). The amount is FIXED by the booking's snapshot terms (R4) — no client-supplied
 * amount is ever sent; the charge endpoint takes no body.
 */
import { customInstance } from "@repo/api-client";
import type { GuaranteeResponse } from "@repo/api-client";
import { STEP_UP_HEADER } from "../../bookings/bookingsApi";

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}`;

export const noShowChargeApi = {
  /** The booking's guarantee record: snapshot fee + card brand/last4 + charge state. */
  get: (venueId: string, bookingId: string, signal?: AbortSignal) =>
    customInstance<GuaranteeResponse>(
      `${base(venueId)}/bookings/${encodeURIComponent(bookingId)}/guarantee`,
      { method: "GET", signal },
    ),

  /**
   * Manager-confirmed no-show charge (R4/R6): NO body (amount = the booking's snapshot, never client
   * supplied), the minted `NO_SHOW_CHARGE` authorization as the step-up header. Single-charge guard +
   * one-way `paymentStatus` are enforced server-side (BK-11).
   */
  charge: (venueId: string, bookingId: string, stepUpAuthId: string) =>
    customInstance<GuaranteeResponse>(
      `${base(venueId)}/bookings/${encodeURIComponent(bookingId)}/guarantee/charge`,
      {
        method: "POST",
        headers: stepUpAuthId ? { [STEP_UP_HEADER]: stepUpAuthId } : {},
      },
    ),

  /** Refund a wrongly-triggered charge via billing's refund path; `paymentStatus` → REFUNDED. */
  refund: (venueId: string, bookingId: string, reason: string) =>
    customInstance<GuaranteeResponse>(
      `${base(venueId)}/bookings/${encodeURIComponent(bookingId)}/guarantee/refund`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      },
    ),

  /** Guarantees whose charge FAILED — surfaced for manager follow-up, no auto-retry (R10). */
  failed: (venueId: string, signal?: AbortSignal) =>
    customInstance<GuaranteeResponse[]>(`${base(venueId)}/guarantees/failed`, {
      method: "GET",
      signal,
    }),
};

/** Format integer minor units as a localized currency amount (ADR-0024 — never a float in transit). */
export function formatFee(feeMinor: number, currency: string, locale = "fr-FR"): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(feeMinor / 100);
}
